import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TopNav } from "@/pages/Home";
import { PaperButton } from "@/components/paper";
import { api, BACKEND_URL, errMsg } from "@/lib/api";
import { audio } from "@/lib/audio";
import { toast } from "sonner";
import { Download, Package, Calendar, HardDrive, Smartphone, X, Sparkles, ExternalLink, MessageSquare, Send } from "lucide-react";

export default function CreatorsHub() {
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [site, setSite] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/hub/app").then((r) => setApp(r.data)).catch(() => setApp({ available: false }));
    api.get("/hub/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <TopNav nav={nav} />
      <section className="max-w-6xl mx-auto px-5 pt-10 pb-8">
        <div className="inline-block font-hand font-bold text-sm px-3 py-1 mb-4 sticky-note bg-sticky-pink -rotate-2"><Sparkles size={14} strokeWidth={2.5} className="inline mr-1" />for creators & communities</div>
        <h1 className="font-hand font-bold text-5xl sm:text-6xl leading-none">LOSTDC CREATORS HUB</h1>
        <p className="font-script text-2xl sm:text-3xl mt-3 text-ink2">Everything LostDC for creators, players and communities.</p>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-10 grid lg:grid-cols-12 gap-8">
        {/* App card */}
        <div className="lg:col-span-7">
          <div className="card-paper p-6 relative">
            <div className="tape" style={{ top: -13, left: "45%", transform: "rotate(-5deg)" }} />
            <div className="flex items-start gap-4">
              <div className="card-flat p-3 bg-sticky-yellow"><Package size={40} strokeWidth={2.5} /></div>
              <div className="flex-1">
                <div className="font-hand font-bold text-3xl">{app?.available ? app.name : "LOSTDC App"}</div>
                {app?.available ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Chip icon={<Sparkles size={14} strokeWidth={2.5} />}>{app.version}</Chip>
                    <Chip icon={<HardDrive size={14} strokeWidth={2.5} />}>{app.sizeLabel}</Chip>
                    <Chip icon={<Smartphone size={14} strokeWidth={2.5} />}>{app.platform}</Chip>
                    {app.date && <Chip icon={<Calendar size={14} strokeWidth={2.5} />}>{new Date(app.date).toLocaleDateString()}</Chip>}
                  </div>
                ) : <div className="font-semibold text-ink2 mt-1">No app has been published yet — check back soon!</div>}
              </div>
            </div>
            {app?.available && (
              <>
                <p className="font-semibold text-ink2 mt-4">{app.description}</p>
                <PaperButton data-testid="open-download" color="#C7EFC2" className="mt-5 text-xl w-full sm:w-auto" onClick={() => { audio.play("click"); setOpen(true); }}>
                  <Download size={20} strokeWidth={2.5} className="inline mr-2" />{app.downloadLabel || "DOWNLOAD APP"}
                </PaperButton>
              </>
            )}
          </div>

          {app?.available && app.releaseNotes && (
            <div className="card-paper p-6 mt-6" style={{ background: "#BAE3FB" }}>
              <div className="font-hand font-bold text-2xl mb-2">What's New — {app.version}</div>
              <p className="font-semibold text-ink2 whitespace-pre-line">{app.releaseNotes}</p>
            </div>
          )}
        </div>

        {/* About + features */}
        <div className="lg:col-span-5 space-y-6">
          <div className="card-paper p-6">
            <div className="font-hand font-bold text-2xl mb-2">About LOSTDC</div>
            <p className="font-semibold text-ink2">{site?.about}</p>
          </div>
          {site?.features?.length > 0 && (
            <div className="space-y-3">
              {site.features.map((f, i) => (
                <div key={i} className="sticky-note p-4" style={{ background: ["#FFE9A8", "#C7EFC2", "#FAC7D9"][i % 3], transform: `rotate(${(i % 2 ? 1 : -1)}deg)` }}>
                  <div className="font-hand font-bold text-lg">{f.title}</div>
                  <div className="font-semibold text-ink2 text-sm">{f.desc}</div>
                </div>
              ))}
            </div>
          )}
          {site?.links && Object.values(site.links).some(Boolean) && (
            <div className="card-paper p-5">
              <div className="font-hand font-bold text-xl mb-2">Links</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(site.links).filter(([, v]) => v).map(([k, v]) => (
                  <a key={k} href={v} target="_blank" rel="noreferrer" className="btn-sketch px-3 py-1.5 text-sm">{k}<ExternalLink size={12} strokeWidth={2.5} className="inline ml-1" /></a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {open && <DownloadModal app={app} requireGmail={site?.requireGmail !== false} onClose={() => setOpen(false)} />}

      <FeedbackSection />
    </div>
  );
}

function FeedbackSection() {
  const [f, setF] = useState({ username: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feedback", f);
      audio.play("success");
      toast.success("Thanks for your feedback!");
      setDone(true);
      setF({ username: "", email: "", message: "" });
    } catch (e) { toast.error(errMsg(e)); audio.play("error"); }
    setBusy(false);
  };
  return (
    <section className="max-w-6xl mx-auto px-5 pb-20">
      <div className="card-paper p-6" style={{ background: "#FFF7D6" }}>
        <div className="tape" style={{ top: -13, left: "12%", transform: "rotate(4deg)" }} />
        <div className="font-hand font-bold text-3xl mb-1 flex items-center gap-2"><MessageSquare strokeWidth={2.5} /> Send Feedback</div>
        <p className="font-semibold text-ink2 mb-4">Tell us what you think! Your name is shown to the LostDC team; your Gmail is used only for identification and stays private to admins.</p>
        {done ? (
          <div className="font-hand font-bold text-xl text-role-artist">🎉 Got it — thank you! You can send more anytime.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="font-hand font-bold">Display name</label>
              <input data-testid="feedback-username" value={f.username} maxLength={24} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="The name you want to show" className="w-full mt-1 card-flat px-3 py-2.5 font-semibold outline-none focus-mark" />
              <label className="font-hand font-bold mt-3 block">Gmail (private, for identification)</label>
              <input data-testid="feedback-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@gmail.com" className="w-full mt-1 card-flat px-3 py-2.5 font-semibold outline-none focus-mark" />
              <p className="text-xs font-semibold text-ink2 mt-1">🔒 Only LostDC admins can see your email.</p>
            </div>
            <div className="flex flex-col">
              <label className="font-hand font-bold">Your feedback</label>
              <textarea data-testid="feedback-message" value={f.message} maxLength={1000} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="What do you love? What should we improve?" className="w-full mt-1 card-flat px-3 py-2.5 font-semibold outline-none focus-mark flex-1" rows={5} />
              <PaperButton data-testid="feedback-submit" color="#C7EFC2" className="mt-3 text-lg" disabled={busy} onClick={submit}><Send size={18} strokeWidth={2.5} className="inline mr-2" />{busy ? "Sending…" : "SUBMIT FEEDBACK"}</PaperButton>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Chip({ children, icon }) {
  return <span className="sticky-note bg-cream font-hand font-bold text-sm px-2.5 py-1 inline-flex items-center gap-1">{icon}{children}</span>;
}

function DownloadModal({ app, requireGmail, onClose }) {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hub/download", { email: email.trim(), optIn });
      audio.play("success");
      toast.success("Starting your download!");
      const a = document.createElement("a");
      a.href = `${BACKEND_URL}${data.downloadUrl}`;
      a.download = data.filename || "lostdc-app";
      document.body.appendChild(a); a.click(); a.remove();
      onClose();
    } catch (e) { toast.error(errMsg(e)); audio.play("error"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(28,27,26,0.45)" }} onClick={onClose}>
      <div className="card-paper p-6 w-full max-w-md animate-pop-in" onClick={(e) => e.stopPropagation()} data-testid="download-modal">
        <div className="flex justify-between items-start">
          <div className="font-hand font-bold text-3xl">GET THE LOSTDC APP</div>
          <button onClick={onClose} className="btn-sketch p-1.5"><X size={18} strokeWidth={2.5} /></button>
        </div>
        <p className="font-semibold text-ink2 mt-1">{app?.name} · {app?.version} · {app?.sizeLabel}</p>
        <label className="font-hand font-bold text-lg mt-4 block">Enter your {requireGmail ? "Gmail" : "email"} address</label>
        <input data-testid="download-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={requireGmail ? "you@gmail.com" : "you@email.com"} className="w-full mt-2 card-flat px-4 py-3 font-semibold text-lg outline-none focus-mark" />
        <label className="flex items-start gap-2 mt-3 cursor-pointer">
          <input type="checkbox" data-testid="download-optin" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-1" />
          <span className="font-semibold text-sm text-ink2">I want to receive LostDC updates</span>
        </label>
        <p className="text-xs font-semibold text-ink2 mt-2">Your email is used to record the app download and may be used for LostDC product updates if you explicitly opt in.</p>
        <PaperButton data-testid="download-submit" color="#C7EFC2" className="w-full mt-4 text-xl" disabled={busy} onClick={submit}>{busy ? "Please wait…" : "DOWNLOAD NOW"}</PaperButton>
      </div>
    </div>
  );
}
